// 加勢（式神の作り直し）：仲間の式神（10種族）からキャラごとに1体を「加勢」に選ぶと、戦闘で条件を満たしたとき自動でカットインして技を使う。
// 式神はキャラごと（Profile.kits）。TOP・出撃画面・結果画面は出撃キャラの、編成はそのキャラの加勢を変える（data-comp-char）。
// 表示の部品（顔・技の説明）と、変更の札（下から出る）・API 呼び出しをここに集める。
import { COMPANION_EVENT_LABEL, companionKit, companionIdOf, companionOf, kaseiInfo, type CharId, ENEMIES, SHIKIGAMI, SHIKIGAMI_IDS, shikiFormName, shikiLevel, type Profile } from '@hyakki/core';
import { api } from '../api';
import { shikiArtHtml } from '../assets';
import { act, state } from '../state';
import { esc } from './dom';
import { icon } from './icons';
import { openModal, toast } from './overlay';
import { errorText } from './status';

/** 加勢の技の主な効果ごとの小さな印 */
const KIND_ICON: Record<string, string> = {
  build: 'build', mat: 'wall', heal: 'heart', skill: 'skill', surge: 'burst', guard: 'guard', ward: 'shield', bind: 'move', blast: 'burst',
  burn: 'burst', move: 'feather', paint: 'flag', might: 'attack', power: 'attack', quake: 'castle', repair: 'wall',
};
/** 式神ごとの小さな印（加勢の技の最初の効果） */
export const companionIcon = (enemyId: string | undefined) => KIND_ICON[kaseiInfo(enemyId ?? '', 1, 1)?.effects[0]?.[0] ?? ''] ?? 'shiki';
/** 戦闘の companion イベント（部品のキー）の浮き文字 */
export const companionEventText = (ability: string) => COMPANION_EVENT_LABEL[ability] ?? '';
/** 浮き文字の能力 → フラグのキー（comp:<キー>） */
export const companionEventKey = (ability: string) => ({ kamaitachi: 'dash', hitotsume: 'trap' })[ability] ?? ability;
/** その式神の加勢がこの能力を持つか（スキル由来の comp:* と区別する。加勢は comp:* を持たない） */
export const companionHas = (enemyId: string | undefined, ability: string) => !!enemyId && companionKit(enemyId, 1, 3).flags.some((f) => f.startsWith(`comp:${companionEventKey(ability)}=`));

const stars = (n: number) => `<span class="cp-stars" aria-label="★${n}">${'★'.repeat(n)}</span>`;
/** 「式神Lv12」（主Lv・妖力と紛れないよう必ず「式神Lv」） */
const lvTag = (level: number) => `<span class="cp-lv">式神Lv${level}</span>`;

/** 加勢にしたときに働くもの（技・定期の加護）の短い説明。戦闘の情報札・加勢の札 */
export function companionKitText(enemyId: string, star: number, level: number, withName = true): string {
  const k = kaseiInfo(enemyId, Math.max(1, level), Math.max(1, star));
  if (!k) return '';
  return `${withName ? `${k.name}：` : ''}${k.when}、${k.desc}（1戦に${k.uses}回）／加護：${k.boon.desc}`;
}

/** TOP の出撃ボタンの近くに置く小さな加勢の表示（顔・名前）。押すと変更の札 */
export function companionChipHtml(p: Profile | null = state.profile): string {
  const c = p ? companionOf(p) : null;
  if (!c) {
    const owned = p ? Object.entries(p.shikigami ?? {}).some(([id, s]) => SHIKIGAMI[id] && s.star > 0) : false;
    return owned ? `<button class="cp-chip empty" data-comp-open aria-label="召喚する式神を選ぶ">${icon('shiki')}<span>式神召喚を選ぶ</span></button>` : '';
  }
  return `<button class="cp-chip" data-comp-open aria-label="式神召喚：${esc(c.name)}。押すと変更">
    <span class="cp-chip-face">${shikiArtHtml(c.enemyId, 74, c.star)}</span><span class="cp-chip-txt"><small>式神召喚</small><b>${esc(c.name)}</b></span><i class="cp-chip-ab">${icon(companionIcon(c.enemyId))}</i></button>`;
}

/** 加勢を変える（null で外す）。charId を省くと出撃キャラ。成功したら /api/me を取り直す */
export async function setCompanion(enemyId: string | null, charId?: CharId): Promise<boolean> {
  try {
    await act(api.setCompanion(enemyId, charId));
    const st = enemyId ? state.profile?.shikigami?.[enemyId]?.star ?? 1 : 1;
    toast(enemyId ? `${shikiFormName(enemyId, st)}を式神召喚にセットしました` : '式神召喚を外しました', 'good');
    return true;
  } catch (err) {
    toast(errorText(err), 'error');
    return false;
  }
}

/** 加勢を選ぶ札（下から出る）。10種族を1行ずつ（仲間は★・式神Lv の高い順、まだの式神は鍵） */
export function openCompanionSheet(onDone?: () => void, charId?: CharId) {
  const p = state.profile;
  if (!p) return;
  const seen = new Set(p.dex ?? []);
  const starOf = (id: string) => p.shikigami?.[id]?.star ?? 0;
  const lvOf = (id: string) => shikiLevel(p.shikigami?.[id]);
  const ids = [...SHIKIGAMI_IDS].sort((a, b) => Number(starOf(b) > 0) - Number(starOf(a) > 0) || starOf(b) - starOf(a) || lvOf(b) - lvOf(a) || (ENEMIES[a]?.no ?? 0) - (ENEMIES[b]?.no ?? 0));
  const n = ids.filter((id) => starOf(id) > 0).length;
  const rows = ids
    .map((id) => {
      const star = starOf(id);
      const lv = lvOf(id);
      const k = kaseiInfo(id, lv, Math.max(1, star))!;
      const on = star > 0 && id === companionIdOf(p, charId);
      const known = star > 0 || seen.has(id);
      return `<li><button class="cp-row ${on ? 'on' : ''} ${star >= 3 ? 'star5' : ''}" ${star > 0 ? `data-comp-pick="${esc(id)}"` : 'disabled'} aria-pressed="${on}">
        <span class="cp-face">${star > 0 ? shikiArtHtml(id, 40) : `<i class="cp-lock">${icon('lock')}</i>`}</span>
        <span class="cp-row-txt"><b>${icon(companionIcon(id))}${esc(k.name)}<em>${known ? esc(shikiFormName(id, Math.max(1, star))) : '？？？'}${star > 0 ? `${stars(star)}${lvTag(lv)}` : `<span class="muted">・${esc(SHIKIGAMI[id].role2)}</span>`}</em></b>
        <small>${esc(star > 0 ? companionKitText(id, star, lv, false) : `${k.when}、${k.desc}`)}</small></span></button></li>`;
    })
    .join('');
  const m = openModal(
    `<ul class="cp-list" role="list">${rows}</ul>
     <div class="cp-foot"><p class="muted small">仲間 ${n}/${SHIKIGAMI_IDS.length}</p>${companionIdOf(p, charId) ? '<button class="btn" data-comp-pick="">外す</button>' : ''}</div>`,
    { title: '召喚する式神を選ぶ', className: 'sheet-wrap navy-sheet cp-sheet' },
  );
  m.body.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-comp-pick]');
    if (!b || b.disabled) return;
    const id = b.dataset.compPick || null;
    if (id === companionIdOf(p, charId)) return m.close();
    b.disabled = true;
    const ok = await setCompanion(id, charId);
    if (ok) {
      m.close();
      onDone?.();
    } else b.disabled = false;
  });
}

/** root の中の [data-comp-open]（札を開く）・[data-comp-set]（その式神を加勢にする）を扱う。戻り値で外す */
export function bindCompanionActions(root: HTMLElement, onDone?: () => void): () => void {
  const h = async (e: Event) => {
    const t = e.target as HTMLElement;
    const open = t.closest<HTMLButtonElement>('[data-comp-open]');
    if (open && !open.disabled) {
      e.stopPropagation();
      openCompanionSheet(onDone, (open.dataset.compChar as CharId | undefined) || undefined);
      return;
    }
    const set = t.closest<HTMLButtonElement>('[data-comp-set]');
    if (set && !set.disabled) {
      e.stopPropagation();
      set.disabled = true;
      if (await setCompanion(set.dataset.compSet!, (set.dataset.compChar as CharId | undefined) || undefined)) onDone?.();
      else set.disabled = false;
    }
  };
  root.addEventListener('click', h);
  return () => root.removeEventListener('click', h);
}
